import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, DownloadSimple, TrashSimple, Image as ImageIcon, Paperclip, Table, ChartBar, FileArchive, CircleNotch, ShieldCheck, WarningCircle, X, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { engApi } from './engApi';
import { EngFormCharts } from './EngFormCharts';
import { formatJST } from '@/utils/datetime';

function AnswerCell({ field, value }) {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return <span className="text-zinc-300">—</span>;
  }
  if (field.type === 'photo' && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v) => (
          <a key={v.file_id} href={engApi.formFileUrl(v.file_id)} target="_blank" rel="noreferrer">
            <img src={engApi.formFileUrl(v.file_id)} alt={v.filename}
              className="h-12 w-12 object-cover rounded border border-zinc-200 hover:opacity-80" />
          </a>
        ))}
      </div>
    );
  }
  if (field.type === 'file' && Array.isArray(value)) {
    return (
      <div className="space-y-0.5">
        {value.map((v) => (
          <a key={v.file_id} href={engApi.formFileUrl(v.file_id)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-teal-700 hover:underline text-xs">
            <Paperclip size={11} /> {v.filename}
          </a>
        ))}
      </div>
    );
  }
  if (Array.isArray(value)) return <span>{value.join('、')}</span>;
  return <span className="whitespace-pre-wrap break-words">{String(value)}</span>;
}

export default function EngFormResponses() {
  const navigate = useNavigate();
  const { fid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [view, setView] = useState('table');
  const [zipProg, setZipProg] = useState(null);
  const [attCheck, setAttCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [reqBusy, setReqBusy] = useState(null);
  const [reqDone, setReqDone] = useState({});

  const requestReupload = async (b, key) => {
    if (reqBusy || reqDone[key]) return;
    if (!b.responder_user_id) return;
    setReqBusy(key);
    try {
      const res = await engApi.requestFormReupload(fid, b.response_id, {
        filename: b.filename, field_label: b.field_label, no: b.no,
      });
      setReqDone((p) => ({ ...p, [key]: true }));
      toast.success(`${res.responder_name || '回答者'} さんへ再アップロード依頼を送信しました`);
    } catch (e) {
      const msg = e?.response?.data?.detail || '送信に失敗しました';
      toast.error(msg);
    } finally {
      setReqBusy(null);
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const bulkRequestReupload = async () => {
    if (bulkBusy) return;
    const items = (attCheck?.broken || [])
      .filter((b) => b.responder_user_id
        && !reqDone[`${b.response_id}::${b.file_id || b.filename || ''}`])
      .map((b) => ({ response_id: b.response_id, filename: b.filename,
                     field_label: b.field_label, no: b.no }));
    if (!items.length) { toast.error('送信可能な依頼がありません'); return; }
    setBulkBusy(true);
    try {
      const res = await engApi.bulkRequestFormReupload(fid, {
        items, extra_note: (bulkNote || '').trim() || null,
      });
      const key_of = (b) => `${b.response_id}::${b.file_id || b.filename || ''}`;
      setReqDone((p) => {
        const n = { ...p };
        for (const s of res.sent || []) {
          for (const b of (attCheck?.broken || [])) {
            if (b.responder_user_id === s.responder_id) n[key_of(b)] = true;
          }
        }
        return n;
      });
      setBulkOpen(false);
      setBulkNote('');
      const sent = (res.sent || []).length;
      const skipped = (res.skipped || []).length;
      toast.success(`${sent}名へ一括送信しました${skipped ? `（${skipped}件はスキップ）` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '一括依頼に失敗しました');
    } finally {
      setBulkBusy(false);
    }
  };

  const runAttCheck = async () => {
    if (checking) return;
    setChecking(true);
    try {
      const res = await engApi.checkFormAttachments(fid);
      setAttCheck(res);
      if ((res.broken || []).length === 0) {
        toast.success(`すべての添付ファイルは正常です（${res.total_attachments}件確認）`);
      } else {
        toast.warning(`${res.broken.length}件の添付ファイルに問題が見つかりました`);
      }
    } catch {
      toast.error('添付ファイルの検査に失敗しました');
    } finally {
      setChecking(false);
    }
  };

  const downloadZip = async () => {
    if (zipProg) return;
    setZipProg({ bytes: 0, files: 0, part: 0, parts: 1 });
    try {
      const info = await fetch(`${engApi.formExportZipUrl(fid).replace('/export.zip', '/export/parts')}`, { credentials: 'include' }).then((r) => r.json());
      const parts = Math.max(1, info.parts || 1);
      for (let p = 1; p <= parts; p += 1) {
        const url = parts > 1 ? `${engApi.formExportZipUrl(fid)}?part=${p}` : engApi.formExportZipUrl(fid);
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error('export failed');
        const totalFiles = Number(res.headers.get('X-Export-Total-Files') || 0);
        const reader = res.body.getReader();
        const chunks = [];
        let bytes = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          bytes += value.length;
          setZipProg({ bytes, files: totalFiles, part: p, parts });
        }
        const blob = new Blob(chunks, { type: 'application/zip' });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${data?.form?.title || 'form'}_responses${parts > 1 ? `_part${p}` : ''}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      }
      toast.success(parts > 1 ? `ZIPを${parts}パートに分割してダウンロードしました（各100MB以下）` : 'ZIPをダウンロードしました');
    } catch {
      toast.error('ZIPエクスポートに失敗しました');
    } finally {
      setZipProg(null);
    }
  };

  const load = useCallback(() => {
    engApi.formResponses(fid).then(setData).catch((e) => setErr(e?.response?.data?.detail || '回答の取得に失敗しました'));
  }, [fid]);
  useEffect(() => { load(); }, [load]);

  const removeResponse = async (r) => {
    if (!window.confirm('この回答を削除しますか？')) return;
    try {
      await engApi.deleteFormResponse(r.id);
      toast.success('削除しました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  if (err) return <div className="py-16 text-center text-sm text-rose-600">{err}</div>;
  if (!data) return <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>;

  const { form, responses } = data;
  const fields = form.fields || [];

  return (
    <div className="space-y-5" data-testid="eng-form-responses">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/engineering/forms')} className="p-2 rounded-lg hover:bg-zinc-100" data-testid="eng-resp-back">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">{form.title} — 回答</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{responses.length}件の回答</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={runAttCheck} disabled={checking}
            data-testid="eng-resp-att-check"
            className="border-amber-200 text-amber-700 hover:bg-amber-50"
            title="全回答の添付ファイルが保存先に実在するかを検査します">
            {checking ? (
              <span className="flex items-center gap-1.5"><CircleNotch size={16} className="animate-spin" /> 検査中…</span>
            ) : (
              <><ShieldCheck size={16} className="mr-1" /> 添付ファイル検査</>
            )}
          </Button>
          <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-0.5 h-10" data-testid="eng-resp-view-toggle">
            <button type="button" onClick={() => setView('table')} data-testid="eng-resp-view-table"
              className={`inline-flex items-center gap-1.5 px-3 rounded-md text-xs font-medium transition-colors ${
                view === 'table' ? 'bg-teal-600 text-white shadow-sm' : 'text-zinc-500 hover:bg-zinc-50'}`}>
              <Table size={14} /> テーブル
            </button>
            <button type="button" onClick={() => setView('charts')} data-testid="eng-resp-view-charts"
              className={`inline-flex items-center gap-1.5 px-3 rounded-md text-xs font-medium transition-colors ${
                view === 'charts' ? 'bg-teal-600 text-white shadow-sm' : 'text-zinc-500 hover:bg-zinc-50'}`}>
              <ChartBar size={14} /> グラフ
            </button>
          </div>
          <a href={engApi.formExportUrl(fid)} data-testid="eng-resp-export">
            <Button variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50">
              <DownloadSimple size={16} className="mr-1" /> CSVエクスポート
            </Button>
          </a>
          <Button variant="outline" onClick={downloadZip} disabled={!!zipProg}
            data-testid="eng-resp-export-zip"
            className="border-teal-200 text-teal-700 hover:bg-teal-50 min-w-[220px]"
            title="CSVと添付ファイル（画像・PDF等）を一括ダウンロード">
            {zipProg ? (
              <span className="flex items-center gap-2" data-testid="eng-resp-zip-progress">
                <CircleNotch size={16} className="animate-spin" />
                {zipProg.parts > 1 ? `パート ${zipProg.part}/${zipProg.parts} — ` : ''}圧縮中… {(zipProg.bytes / 1048576).toFixed(1)}MB
                {zipProg.files > 0 ? `（ファイル ${zipProg.files} 件）` : ''}
              </span>
            ) : (
              <><FileArchive size={16} className="mr-1" /> ZIP（画像・ファイル込み）</>
            )}
          </Button>
        </div>
      </div>

      {attCheck && (attCheck.broken || []).length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="eng-resp-att-broken">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-rose-800 flex items-center gap-1.5">
              <WarningCircle size={17} weight="fill" />
              {attCheck.broken.length}件の添付ファイルが取得できません（全{attCheck.total_attachments}件中）— 回答者に再アップロードを依頼してください
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setBulkOpen(true)}
                className="inline-flex items-center gap-1 rounded-full border border-rose-400 bg-rose-100 px-2.5 py-0.5 text-[11px] font-semibold text-rose-800 hover:bg-rose-200 transition-colors"
                data-testid="eng-resp-att-broken-bulk-btn">
                <PaperPlaneTilt size={12} weight="fill" /> 回答者全員に一括依頼
              </button>
              <button onClick={() => setAttCheck(null)} className="text-rose-400 hover:text-rose-600" data-testid="eng-resp-att-broken-close">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="mt-2 space-y-1">
            {attCheck.broken.map((b, i) => {
              const key = `${b.response_id}::${b.file_id || b.filename || i}`;
              const done = !!reqDone[key];
              const busy = reqBusy === key;
              const canSend = !!b.responder_user_id && !done;
              const tooltip = !b.responder_user_id
                ? '匿名回答のためチャット送信できません'
                : (done ? '送信済み' : '回答者へ再アップロードを依頼します');
              return (
                <div key={key} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-rose-700" data-testid={`eng-resp-att-broken-${i}`}>
                  <span className="font-mono font-semibold">No.{String(b.no).padStart(3, '0')}</span>
                  <span className="font-medium">{b.filename}</span>
                  {b.field_label && <span className="text-rose-500">（{b.field_label}）</span>}
                  <span className="text-rose-500">{b.submitted_by} / {formatJST(b.created_at)}</span>
                  <span className="rounded bg-rose-100 px-1.5 py-px text-[10px]">{b.reason}</span>
                  <button
                    onClick={() => requestReupload(b, key)}
                    disabled={!canSend || busy}
                    title={tooltip}
                    className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition ${
                      done
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700 cursor-default'
                        : canSend
                          ? 'border-rose-300 bg-white text-rose-700 hover:bg-rose-100'
                          : 'border-zinc-200 bg-zinc-50 text-zinc-400 cursor-not-allowed'
                    }`}
                    data-testid={`eng-resp-att-broken-reupload-${i}`}
                  >
                    {done ? (
                      <><CheckCircle size={12} weight="fill" /> 依頼済み</>
                    ) : busy ? (
                      <><CircleNotch size={12} className="animate-spin" /> 送信中…</>
                    ) : (
                      <><PaperPlaneTilt size={12} weight="fill" /> 再アップロード依頼</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {attCheck && (attCheck.broken || []).length === 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between" data-testid="eng-resp-att-ok">
          <p className="text-sm text-emerald-800 flex items-center gap-1.5">
            <ShieldCheck size={17} weight="fill" /> すべての添付ファイルは正常です（{attCheck.total_attachments}件確認）
          </p>
          <button onClick={() => setAttCheck(null)} className="text-emerald-400 hover:text-emerald-600" data-testid="eng-resp-att-ok-close">
            <X size={16} />
          </button>
        </div>
      )}

      {responses.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-10 text-center" data-testid="eng-resp-empty">
          <ImageIcon size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">まだ回答がありません</p>
        </div>
      ) : view === 'charts' ? (
        <EngFormCharts form={form} responses={responses} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="eng-resp-table">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                <th className="px-3 py-2 text-xs font-semibold text-zinc-500 whitespace-nowrap" title="ZIPエクスポート内のファイル番号（回答番号）">No.</th>
                <th className="px-3 py-2 text-xs font-semibold text-zinc-500 whitespace-nowrap">回答日時</th>
                <th className="px-3 py-2 text-xs font-semibold text-zinc-500 whitespace-nowrap">回答者</th>
                {fields.map((f) => (
                  <th key={f.id} className="px-3 py-2 text-xs font-semibold text-zinc-500 min-w-[120px]">{f.label}</th>
                ))}
                <th className="px-3 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {responses.map((r, i) => (
                <tr key={r.id} className="border-b border-zinc-200 hover:bg-teal-50/50 align-top" data-testid={`eng-resp-row-${r.id}`}>
                  <td className="px-3 py-2 text-xs font-semibold text-teal-700 whitespace-nowrap tabular-nums" data-testid={`eng-resp-no-${r.id}`}>
                    {String(responses.length - i).padStart(3, '0')}
                    {(attCheck?.broken || []).some((b) => b.response_id === r.id) && (
                      <WarningCircle size={13} weight="fill" className="ml-1 inline text-rose-500" title="添付ファイルに問題があります" />
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap tabular-nums">
                    {formatJST(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    <span className="font-medium text-zinc-800">{r.submitted_by_name}</span>
                    {r.department_name && <span className="block text-[10px] text-zinc-400">{r.department_name}</span>}
                  </td>
                  {fields.map((f) => (
                    <td key={f.id} className="px-3 py-2 text-xs text-zinc-700">
                      <AnswerCell field={f} value={(r.answers || {})[f.id]} />
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <button onClick={() => removeResponse(r)} className="text-rose-500 hover:text-rose-700" data-testid={`eng-resp-delete-${r.id}`}>
                      <TrashSimple size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => !bulkBusy && setBulkOpen(false)} data-testid="eng-resp-bulk-modal">
          <div className="w-full max-w-xl rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-zinc-200">
              <h3 className="text-base font-bold text-zinc-800 flex items-center gap-2">
                <PaperPlaneTilt size={16} className="text-rose-500" weight="fill" /> 回答者全員に一括依頼
              </h3>
              <p className="mt-1 text-xs text-zinc-500">
                {(() => {
                  const targets = (attCheck?.broken || []).filter((b) => b.responder_user_id
                    && !reqDone[`${b.response_id}::${b.file_id || b.filename || ''}`]);
                  const uniq = new Set(targets.map((b) => b.responder_user_id));
                  return `${uniq.size}名の回答者に ${targets.length}件の再アップロード依頼を送信します。同じ回答者への依頼は1通のDMにまとめられます。`;
                })()}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600 mb-1 block">
                  追加メッセージ（任意 — 送信文面の末尾に付与されます）
                </label>
                <textarea
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value.slice(0, 1000))}
                  rows={4}
                  placeholder="例: 至急ご対応をお願いいたします。"
                  className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-rose-400"
                  data-testid="eng-resp-bulk-note"
                />
                <p className="mt-1 text-[10px] text-zinc-400 text-right">{bulkNote.length} / 1000</p>
              </div>
              <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">メッセージプレビュー</p>
                <pre className="text-[11px] text-zinc-700 whitespace-pre-wrap font-sans" data-testid="eng-resp-bulk-preview">
{`【添付ファイル再アップロードのお願い（一括）】\n\nフォーム『${form?.title || ''}』の以下の添付ファイルが取得できませんでした。\nお手数ですが、下記リンクから再度アップロードをお願いいたします。\n\n→ /f/${fid}\n\n▼ 対象:\n  ・(該当項目のリストがここに入ります)`}
{bulkNote.trim() ? `\n\n${bulkNote.trim()}` : ''}
                </pre>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200 bg-zinc-50 rounded-b-2xl">
              <button onClick={() => setBulkOpen(false)} disabled={bulkBusy}
                className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 disabled:opacity-40"
                data-testid="eng-resp-bulk-cancel">キャンセル</button>
              <button onClick={bulkRequestReupload} disabled={bulkBusy}
                className="inline-flex items-center gap-1 rounded-lg bg-rose-600 text-white px-4 py-1.5 text-xs font-semibold hover:bg-rose-700 disabled:opacity-50"
                data-testid="eng-resp-bulk-send">
                {bulkBusy ? <><CircleNotch size={12} className="animate-spin" /> 送信中…</> : <><PaperPlaneTilt size={12} weight="fill" /> 一括送信</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
